import { normalizeMapWindowLayoutChangeV1 } from "./map-control-events.js";
import { normalizeWindowUiState } from "./map-signal-contract.js";

const DRAG_THRESHOLD_PX = 4;
const WINDOW_TITLEBAR_FALLBACK_HEIGHT_PX = 56;
const MOBILE_MAP_WINDOW_MEDIA_QUERY = "(max-width: 960px)";
const WINDOW_DRAG_IGNORE_SELECTOR =
  "input, textarea, select, button, a, label, summary, [data-window-drag-ignore='true']";

function isFiniteCoordinate(value) {
  return Number.isFinite(value);
}

export function clampManagedWindowPosition(shellRect, rootRect, titlebarHeight, x, y) {
  return {
    x: Math.max(0, Math.min(Math.round(x), Math.max(0, Math.round(shellRect.width - Math.min(rootRect.width, shellRect.width))))),
    y: Math.max(0, Math.min(Math.round(y), Math.max(0, Math.round(shellRect.height - titlebarHeight)))),
  };
}

function shouldIgnoreWindowDragTarget(target) {
  return Boolean(target?.closest?.(WINDOW_DRAG_IGNORE_SELECTOR));
}

function currentWindowUi(getSignals) {
  return normalizeWindowUiState(getSignals?.()?._map_ui?.windowUi);
}

function currentManagedWindowPosition(shell, part, entry) {
  const shellRect = shell.getBoundingClientRect?.();
  const rootRect = part.root.getBoundingClientRect?.();
  const rectX = Number(rootRect?.left) - Number(shellRect?.left);
  const rectY = Number(rootRect?.top) - Number(shellRect?.top);
  if (isFiniteCoordinate(rectX) && isFiniteCoordinate(rectY)) {
    return {
      x: Math.round(rectX),
      y: Math.round(rectY),
    };
  }
  return {
    x: isFiniteCoordinate(entry?.x) ? entry.x : 0,
    y: isFiniteCoordinate(entry?.y) ? entry.y : 0,
  };
}

function applyManagedWindowPosition(root, entry) {
  if (isFiniteCoordinate(entry?.x) && isFiniteCoordinate(entry?.y)) {
    root.style.left = `${entry.x}px`;
    root.style.top = `${entry.y}px`;
    root.style.right = "auto";
    root.style.bottom = "auto";
    if (
      root.id === "fishymap-search-window"
      || root.id === "fishymap-help-window"
    ) {
      root.style.transform = "none";
    }
    return;
  }
  root.style.removeProperty("left");
  root.style.removeProperty("top");
  root.style.removeProperty("right");
  root.style.removeProperty("bottom");
  root.style.removeProperty("transform");
}

function applyMobileManagedWindowPosition(root, entry) {
  applyManagedWindowPosition(root, entry);
  if (isFiniteCoordinate(entry?.x) && isFiniteCoordinate(entry?.y)) {
    root.style.removeProperty("bottom");
  }
}

function defaultIsMobileLayout() {
  return globalThis.matchMedia?.(MOBILE_MAP_WINDOW_MEDIA_QUERY)?.matches === true;
}

export function createMapWindowManager({
  shell,
  getSignals,
  onWindowLayoutChange = () => false,
  isMobileLayout = defaultIsMobileLayout,
} = {}) {
  if (!shell || typeof shell.querySelectorAll !== "function") {
    throw new Error("createMapWindowManager requires a shell element");
  }

  const parts = Object.fromEntries(
    Array.from(shell.querySelectorAll("[data-window-id]")).map((root) => {
      const windowId = String(root.getAttribute("data-window-id") || "").trim();
      return [
        windowId,
        {
          root,
          titlebar: shell.querySelector(`[data-window-titlebar="${windowId}"]`),
        },
      ];
    }).filter(([windowId]) => windowId),
  );
  const toolbarButtons = Array.from(
    shell.querySelectorAll("[data-window-toggle]"),
  ).map((button) => ({
    button,
    windowId: String(button.getAttribute("data-window-toggle") || "").trim(),
  })).filter(({ windowId }) => Boolean(parts[windowId]));
  const removeOwnedListeners = [];

  const state = {
    previousWindowUi: currentWindowUi(getSignals),
    nextWindowZIndex: 24,
    frontWindowId: null,
    frameId: 0,
    mobileLayout: false,
    mobileSelectionInitialized: false,
    mobileActiveWindowId: null,
    mobilePositions: new Map(),
    drag: {
      windowId: null,
      pointerId: null,
      titlebar: null,
      startClientX: 0,
      startClientY: 0,
      originX: 0,
      originY: 0,
      moved: false,
    },
    destroyed: false,
  };

  function emitWindowLayoutChange(detail) {
    return onWindowLayoutChange(normalizeMapWindowLayoutChangeV1(detail)) === true;
  }

  function listen(target, type, handler) {
    if (typeof target?.addEventListener !== "function") {
      return;
    }
    target.addEventListener(type, handler);
    removeOwnedListeners.push(() => target.removeEventListener?.(type, handler));
  }

  function bringToFront(windowId) {
    const root = parts[windowId]?.root;
    if (!root) {
      return highestWindowZIndex();
    }
    if (state.frontWindowId === windowId) {
      return Number.parseInt(root.style.zIndex, 10) || highestWindowZIndex();
    }
    state.nextWindowZIndex += 1;
    root.style.zIndex = String(state.nextWindowZIndex);
    state.frontWindowId = windowId;
    return state.nextWindowZIndex;
  }

  function highestWindowZIndex() {
    return Object.values(parts).reduce(
      (highest, part) =>
        Math.max(highest, Number.parseInt(part.root?.style?.zIndex, 10) || 20),
      20,
    );
  }

  function shouldApplyWindowPositionFromPatch(windowId, patch) {
    const windowUiPatch = patch && patch._map_ui && typeof patch._map_ui === "object"
      ? patch._map_ui.windowUi
      : null;
    if (!windowUiPatch || typeof windowUiPatch !== "object") {
      return true;
    }
    const nextWindowUiPatch = windowUiPatch[windowId];
    if (!nextWindowUiPatch || typeof nextWindowUiPatch !== "object") {
      return false;
    }
    return (
      Object.prototype.hasOwnProperty.call(nextWindowUiPatch, "x")
      || Object.prototype.hasOwnProperty.call(nextWindowUiPatch, "y")
    );
  }

  function clampedWindowPosition(part, x, y, mobileLayout) {
    const shellRect = shell.getBoundingClientRect();
    const dragBounds = {
      width: shellRect.width,
      height: shellRect.height,
    };
    if (mobileLayout) {
      const toolbarRect = shell.querySelector?.("#fishymap-toolbar")?.getBoundingClientRect?.();
      const toolbarTop = Number(toolbarRect?.top) - Number(shellRect?.top);
      if (Number.isFinite(toolbarTop) && toolbarTop > 0) {
        dragBounds.height = Math.min(shellRect.height, toolbarTop);
      }
    }
    const rootRect = part.root.getBoundingClientRect();
    const titlebarHeight = part.titlebar?.offsetHeight
      || part.titlebar?.getBoundingClientRect?.().height
      || WINDOW_TITLEBAR_FALLBACK_HEIGHT_PX;
    return clampManagedWindowPosition(
      dragBounds,
      rootRect,
      titlebarHeight,
      x,
      y,
    );
  }

  function desktopWindowPresentationPosition(part, entry) {
    if (!isFiniteCoordinate(entry?.x) || !isFiniteCoordinate(entry?.y)) {
      return entry;
    }
    // Saved coordinates remain the user's preferred layout. Viewport fitting is
    // a presentation projection and must not become a signal or preset edit.
    return clampedWindowPosition(part, entry.x, entry.y, false);
  }

  function mobileWindowIdFromPatch(patch, open) {
    const windowUiPatch = patch?._map_ui?.windowUi;
    if (!windowUiPatch || typeof windowUiPatch !== "object") {
      return null;
    }
    return Object.keys(parts).find(
      (windowId) => windowUiPatch[windowId]?.open === open,
    ) || null;
  }

  function applyMobileWindowProjection(nextWindowUi, patch) {
    const mobileLayout = isMobileLayout() === true;
    if (!mobileLayout) {
      state.mobileLayout = false;
      state.mobileSelectionInitialized = false;
      state.mobileActiveWindowId = null;
      delete shell.dataset.mobileWindowLayout;
    } else {
      if (!state.mobileLayout) {
        state.mobileSelectionInitialized = false;
        state.mobileActiveWindowId = null;
      }
      state.mobileLayout = true;
      shell.dataset.mobileWindowLayout = "true";

      const openedWindowId = mobileWindowIdFromPatch(patch, true);
      const closedWindowId = mobileWindowIdFromPatch(patch, false);
      if (openedWindowId) {
        state.mobileActiveWindowId = openedWindowId;
      } else if (
        closedWindowId
        && closedWindowId === state.mobileActiveWindowId
      ) {
        state.mobileActiveWindowId = null;
      } else if (
        !state.mobileSelectionInitialized
        || (
          state.mobileActiveWindowId
          && nextWindowUi[state.mobileActiveWindowId]?.open === false
        )
      ) {
        state.mobileActiveWindowId = Object.keys(parts).find(
          (windowId) => nextWindowUi[windowId]?.open !== false,
        ) || null;
      }
      state.mobileSelectionInitialized = true;
    }

    for (const [windowId, part] of Object.entries(parts)) {
      if (mobileLayout) {
        part.root.dataset.mobileActive = String(
          state.mobileActiveWindowId === windowId
          && nextWindowUi[windowId]?.open !== false,
        );
      } else {
        delete part.root.dataset.mobileActive;
      }
    }
    for (const { button, windowId } of toolbarButtons) {
      const active = mobileLayout
        ? state.mobileActiveWindowId === windowId
          && nextWindowUi[windowId]?.open !== false
        : nextWindowUi[windowId]?.open !== false;
      if (mobileLayout) {
        button.dataset.mobileActive = String(active);
      } else {
        delete button.dataset.mobileActive;
      }
      button.dataset.open = String(active);
      button.setAttribute("aria-pressed", String(active));
    }
    return mobileLayout;
  }

  function applyFromSignals(patch = null) {
    if (state.destroyed) {
      return;
    }
    const nextWindowUi = currentWindowUi(getSignals);
    const mobileLayout = applyMobileWindowProjection(nextWindowUi, patch);
    for (const [windowId, part] of Object.entries(parts)) {
      const nextEntry = nextWindowUi[windowId];
      const previousEntry = state.previousWindowUi?.[windowId];
      if (mobileLayout) {
        if (state.drag.windowId !== windowId) {
          const mobilePosition = state.mobilePositions.get(windowId);
          if (mobilePosition) {
            const clamped = clampedWindowPosition(
              part,
              mobilePosition.x,
              mobilePosition.y,
              true,
            );
            state.mobilePositions.set(windowId, clamped);
            applyMobileManagedWindowPosition(part.root, clamped);
          } else {
            applyManagedWindowPosition(part.root, null);
          }
        }
      } else if (state.drag.windowId !== windowId && shouldApplyWindowPositionFromPatch(windowId, patch)) {
        applyManagedWindowPosition(
          part.root,
          desktopWindowPresentationPosition(part, nextEntry),
        );
      }
      if (nextEntry?.open !== false && previousEntry?.open === false) {
        bringToFront(windowId);
      }
    }
    state.previousWindowUi = nextWindowUi;
  }

  function scheduleApplyFromSignals(patch = null) {
    if (state.destroyed) {
      return;
    }
    if (state.frameId || typeof globalThis.requestAnimationFrame !== "function") {
      if (!state.frameId) {
        applyFromSignals(patch);
      }
      return;
    }
    state.frameId = globalThis.requestAnimationFrame(() => {
      state.frameId = 0;
      applyFromSignals(patch);
    });
  }

  function clearDrag() {
    if (state.drag.windowId) {
      delete parts[state.drag.windowId]?.root?.dataset?.dragging;
    }
    state.drag.windowId = null;
    state.drag.pointerId = null;
    state.drag.titlebar = null;
    state.drag.moved = false;
  }

  function finishDrag(toggleOnTap) {
    const windowId = state.drag.windowId;
    const pointerId = state.drag.pointerId;
    const titlebar = state.drag.titlebar;
    const moved = state.drag.moved;
    if (titlebar && pointerId != null && titlebar.hasPointerCapture?.(pointerId)) {
      titlebar.releasePointerCapture(pointerId);
    }
    clearDrag();
    if (!windowId) {
      return;
    }
    if (!moved && toggleOnTap && windowId !== "search") {
      const windowUiState = currentWindowUi(getSignals);
      const currentEntry = windowUiState[windowId];
      emitWindowLayoutChange({
        version: 1,
        kind: "collapsed",
        windowId,
        collapsed: !currentEntry?.collapsed,
      });
      bringToFront(windowId);
      return;
    }
    scheduleApplyFromSignals();
  }

  function handlePointerMove(event) {
    if (state.drag.pointerId !== event.pointerId || !state.drag.windowId) {
      return;
    }
    const part = parts[state.drag.windowId];
    if (!part?.root) {
      finishDrag(false);
      return;
    }
    const deltaX = event.clientX - state.drag.startClientX;
    const deltaY = event.clientY - state.drag.startClientY;
    if (!state.drag.moved && Math.abs(deltaX) < DRAG_THRESHOLD_PX && Math.abs(deltaY) < DRAG_THRESHOLD_PX) {
      return;
    }
    state.drag.moved = true;
    part.root.dataset.dragging = "true";
    const clamped = clampedWindowPosition(
      part,
      state.drag.originX + deltaX,
      state.drag.originY + deltaY,
      isMobileLayout() === true,
    );
    if (isMobileLayout() === true) {
      applyMobileManagedWindowPosition(part.root, clamped);
    } else {
      applyManagedWindowPosition(part.root, clamped);
    }
  }

  function handlePointerUp(event) {
    if (state.drag.pointerId !== event.pointerId || !state.drag.windowId) {
      return;
    }
    const windowId = state.drag.windowId;
    const part = parts[windowId];
    if (state.drag.moved && part?.root) {
      const mobileLayout = isMobileLayout() === true;
      const clamped = clampedWindowPosition(
        part,
        state.drag.originX + (event.clientX - state.drag.startClientX),
        state.drag.originY + (event.clientY - state.drag.startClientY),
        mobileLayout,
      );
      if (mobileLayout) {
        state.mobilePositions.set(windowId, clamped);
        applyMobileManagedWindowPosition(part.root, clamped);
      } else {
        emitWindowLayoutChange({
          version: 1,
          kind: "position",
          windowId,
          x: clamped.x,
          y: clamped.y,
        });
      }
    }
    finishDrag(true);
  }

  function handlePointerCancel(event) {
    if (state.drag.pointerId !== event.pointerId) {
      return;
    }
    finishDrag(false);
  }

  function toggleWindow(windowId) {
    const part = parts[windowId];
    if (!part) {
      return false;
    }
    const windowUiState = currentWindowUi(getSignals);
    const currentEntry = windowUiState[windowId];
    const mobileLayout = isMobileLayout() === true;
    const isActive = mobileLayout
      && state.mobileActiveWindowId === windowId
      && currentEntry?.open !== false;
    const previousMobileProjection = {
      mobileLayout: state.mobileLayout,
      mobileSelectionInitialized: state.mobileSelectionInitialized,
      mobileActiveWindowId: state.mobileActiveWindowId,
    };

    if (mobileLayout && currentEntry?.open !== false && !isActive) {
      state.mobileLayout = true;
      state.mobileSelectionInitialized = true;
      state.mobileActiveWindowId = windowId;
      bringToFront(windowId);
      applyFromSignals();
      return true;
    }

    if (mobileLayout) {
      state.mobileLayout = true;
      state.mobileSelectionInitialized = true;
      state.mobileActiveWindowId = isActive ? null : windowId;
    }
    const dispatched = emitWindowLayoutChange({
      version: 1,
      kind: "open",
      windowId,
      open: isActive ? false : currentEntry?.open === false,
    });
    if (!dispatched && mobileLayout) {
      Object.assign(state, previousMobileProjection);
    }
    if (!isActive) {
      bringToFront(windowId);
    }
    applyFromSignals();
    return dispatched;
  }

  for (const [windowId, part] of Object.entries(parts)) {
    if (!part.titlebar) {
      continue;
    }
    listen(part.titlebar, "pointerdown", (event) => {
      if (event.button !== 0 || shouldIgnoreWindowDragTarget(event.target)) {
        return;
      }
      const windowUiState = currentWindowUi(getSignals);
      const entry = windowUiState[windowId];
      state.drag.windowId = windowId;
      state.drag.pointerId = event.pointerId;
      state.drag.titlebar = part.titlebar;
      state.drag.startClientX = event.clientX;
      state.drag.startClientY = event.clientY;
      const position = currentManagedWindowPosition(shell, part, entry);
      state.drag.originX = position.x;
      state.drag.originY = position.y;
      state.drag.moved = false;
      part.titlebar.setPointerCapture?.(event.pointerId);
      bringToFront(windowId);
    });
  }
  for (const { button, windowId } of toolbarButtons) {
    listen(button, "click", () => toggleWindow(windowId));
  }

  listen(globalThis, "pointermove", handlePointerMove);
  listen(globalThis, "pointerup", handlePointerUp);
  listen(globalThis, "pointercancel", handlePointerCancel);
  listen(globalThis, "resize", scheduleApplyFromSignals);

  applyFromSignals();

  return Object.freeze({
    applyFromSignals,
    bringToFront,
    highestWindowZIndex,
    scheduleApplyFromSignals,
    destroy() {
      if (state.destroyed) {
        return;
      }
      state.destroyed = true;
      if (state.frameId) {
        globalThis.cancelAnimationFrame?.(state.frameId);
        state.frameId = 0;
      }
      clearDrag();
      state.mobilePositions.clear();
      for (const removeListener of removeOwnedListeners.splice(0)) {
        removeListener();
      }
      delete shell.dataset.mobileWindowLayout;
      for (const part of Object.values(parts)) {
        delete part.root.dataset.mobileActive;
      }
      for (const { button } of toolbarButtons) {
        delete button.dataset.mobileActive;
      }
    },
  });
}
